import React from "react";
import { Alert, Box, Button, LinearProgress, Typography } from "@mui/material";
import { toFriendlyStatus } from "./toFriendlyStatus";
import { toFriendlyError } from "./toFriendlyError";

export interface CompactProcessingIndicatorProps {
  /** Job status. Only `FAILED` changes what is shown; anything else renders the progress line. */
  status?: string;
  /**
   * 0 to 100. A number from 1 to 99 draws a determinate bar and a percentage; anything else is
   * indeterminate. It is only trusted once a step has been reported: with no step the number is a
   * leftover from an earlier phase, so the bar stays indeterminate.
   */
  progress?: number;
  /** The backend's technical step text. It is mapped to a friendly line and never shown as-is. */
  currentStep?: string;
  /** The raw failure text. It only selects the friendly reason and is never shown as-is. */
  errorMessage?: string;
  /** When given, the failure alert offers a button that calls it. */
  onRetry?: () => void;
}

const MAX_HEIGHT_PX = 72;

export function isDeterminateProgress(progress: unknown): progress is number {
  return typeof progress === "number" && Number.isFinite(progress) && progress >= 1 && progress < 100;
}

/**
 * One progress bar, one short status line and (only when the progress is known) a percentage, or an
 * inline error with a plain reason. Rendered inline under the Record and Audio controls.
 */
export const CompactProcessingIndicator: React.FC<CompactProcessingIndicatorProps> = ({
  status,
  progress,
  currentStep,
  errorMessage,
  onRetry,
}) => {
  if (status === "FAILED") {
    return (
      <Box data-testid="compact-processing-indicator" sx={{ width: "100%" }}>
        <Alert
          severity="error"
          variant="filled"
          icon={false}
          sx={{
            alignItems: "center",
            py: 0.25,
            "& .MuiAlert-message": { py: 0.5, fontSize: "0.8125rem", lineHeight: 1.25 },
            "& .MuiAlert-action": { py: 0, pl: 1 },
          }}
          action={
            onRetry ? (
              <Button color="inherit" size="small" onClick={onRetry}>
                Try again
              </Button>
            ) : undefined
          }
        >
          {toFriendlyError(errorMessage)}
        </Alert>
      </Box>
    );
  }

  const hasReportedStep = typeof currentStep === "string" && currentStep.trim() !== "";
  const determinate = hasReportedStep && isDeterminateProgress(progress);

  return (
    <Box
      data-testid="compact-processing-indicator"
      sx={{ width: "100%", maxHeight: MAX_HEIGHT_PX, overflow: "hidden" }}
    >
      <LinearProgress
        aria-label="Processing progress"
        variant={determinate ? "determinate" : "indeterminate"}
        value={determinate ? progress : undefined}
        sx={{
          height: 6,
          borderRadius: 3,
          backgroundColor: "rgba(148, 163, 184, 0.24)",
          "& .MuiLinearProgress-bar": { backgroundColor: "hsl(var(--accent))", borderRadius: 3 },
        }}
      />
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 2, mt: 1 }}>
        <Typography
          role="status"
          variant="body2"
          sx={{ color: "rgba(226, 232, 240, 0.86)", fontSize: "0.875rem" }}
        >
          {toFriendlyStatus(currentStep)}
        </Typography>
        {determinate && (
          <Typography variant="body2" sx={{ color: "rgba(226, 232, 240, 0.86)", fontSize: "0.875rem" }}>
            {`${Math.floor(progress)}%`}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default CompactProcessingIndicator;
